import { api } from '@/lib/api/client';
import type {
  ConsentAnswer, ConsentRequirement, ConsentRequirements, ConsentTemplateRow, ConsentTemplatesResponse,
  ConsentTypeId, PatientConsentRecord,
} from './types';

/**
 * Everything the consent screen needs in one call — the live wording, the
 * patient's current answers and which ones are due. See
 * backend/routes/patient-consent.js.
 *
 * `registrationOnly` narrows it to the types the Registration step asks for.
 */
export function fetchConsentRequirements(uhid: string, registrationOnly = false): Promise<ConsentRequirements> {
  return api.get(`/patients/${encodeURIComponent(uhid)}/consent/requirements${registrationOnly ? '?registration=1' : ''}`);
}

/**
 * The live wording for every consent type, with no patient attached — what the
 * Registration step shows, since the patients row is not written until staff
 * press Complete Registration. Same item shape as the requirements call, with
 * every answer `null`/`missing`.
 */
export function fetchActiveConsentForms(registrationOnly = true): Promise<{ items: ConsentRequirement[] }> {
  return api.get(`/consent-templates/active${registrationOnly ? '?registration=1' : ''}`);
}

/** Every answer ever recorded for this patient, superseded ones included. */
export function fetchConsentHistory(uhid: string): Promise<PatientConsentRecord[]> {
  return api.get(`/patients/${encodeURIComponent(uhid)}/consent/history`);
}

/**
 * Records several answers in one transaction — what the Registration consent
 * step submits. Append-only on the server: a previous answer is superseded,
 * never overwritten.
 */
export function recordConsents(uhid: string, consents: ConsentAnswer[], visitId?: number | null): Promise<PatientConsentRecord[]> {
  return api.post(`/patients/${encodeURIComponent(uhid)}/consent`, { consents, visit_id: visitId ?? null });
}

/** The single-answer path, for re-recording one consent from Patient Records. */
export function recordConsent(uhid: string, type: ConsentTypeId, granted: boolean, notes?: string): Promise<PatientConsentRecord> {
  return api.put(`/patients/${encodeURIComponent(uhid)}/consent/${type}`, { granted, notes });
}

// ── Admin (admin:consent) ────────────────────────────────────────

export function fetchConsentTemplates(): Promise<ConsentTemplatesResponse> {
  return api.get('/consent-templates');
}

/**
 * Publishes wording — and creates the consent form itself when `consent_type`
 * is one this hospital does not have yet, which is how "+ Create New Consent
 * Form" works with no code change.
 *
 * Always creates the next version and retires the current one; there is no
 * edit-in-place, by design, because a patient who already consented must keep
 * the exact text they agreed to. Settings the caller omits carry forward from
 * the version being retired.
 */
export function publishConsentTemplate(input: {
  consent_type: ConsentTypeId;
  label?: string;
  title: string;
  body: string;
  description?: string | null;
  validity_days?: number | null;
  is_enabled?: boolean;
  is_mandatory?: boolean;
}): Promise<ConsentTemplateRow> {
  return api.post('/consent-templates', input);
}

/**
 * Changes settings, not wording: Status, Requirement, the expiry period, the
 * form's name, or which version is live. The backend rejects any attempt to
 * send title/body here.
 *
 * Status and Requirement apply to the whole consent form (every version), not
 * just the row identified by `id` — see the route's own comment.
 */
export function updateConsentTemplate(id: number, input: {
  validity_days?: number | null;
  is_active?: boolean;
  is_enabled?: boolean;
  is_mandatory?: boolean;
  label?: string;
  description?: string | null;
}): Promise<ConsentTemplateRow> {
  return api.patch(`/consent-templates/${id}`, input);
}
